import { useEffect, useMemo, useState, useTransition } from "react";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import {
  useEmitterListener,
  useInitViewsAndFilters,
  usePageContext,
} from "@ryde/redux";
import {
  companyIdSelector,
  controlsSF,
  GlobalAppEventEnum,
  GROUPING_GRID_NO_GROUPS,
  NO_GROUPING_QS_VALUE,
  tableColumnsSortingAF,
  tableFiltersSchemaAtomFamily,
  tableGroupingAF,
  tableHelpers,
  tableItemPaginationAF,
  type IPaginationChangeEvent,
} from "@ryde/shared";

export const usePassengersPage = () => {
  const { entity } = usePageContext();
  const [, startTransition] = useTransition();
  const companyId = useRecoilValue(companyIdSelector);
  const tableFilters = useRecoilValue(tableFiltersSchemaAtomFamily(entity));
  const columnSorting = useRecoilValue(tableColumnsSortingAF(entity));
  const columnGrouping = useRecoilValue(tableGroupingAF(entity));
  const controls = useRecoilValue(controlsSF(entity));
  const tableItemPagination = useRecoilValue(
    tableItemPaginationAF([GROUPING_GRID_NO_GROUPS, entity]),
  );

  const [isGridReady, setIsGridReady] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);

  const {
    fetchTableRowsAndGroupValues,
    fetchTableRowsServerFiltered,
    fetchTableServerGroupValuesMoreIsAvailable,
  } = useInitViewsAndFilters({
    entity,
    isPageDependenciesReady: controls.length > 0,
    onInitialized: () => setIsGridReady(true),
    onInitSkipped: () => {
      setIsGridReady(false);
      setIsInitializing(true);
    },
  });

  const hasActiveGrouping = useMemo(() => {
    const groupByField = tableHelpers.getGroupByField(columnGrouping);
    return Boolean(groupByField && groupByField !== NO_GROUPING_QS_VALUE);
  }, [columnGrouping]);

  const refetchGrid = useEvent((args?: { skipPageReset?: boolean }) => {
    fetchTableRowsAndGroupValues({
      isPreloader: true,
      skipPageReset: args?.skipPageReset,
    });
  });

  const onFiltersApply = useEvent((): void => {
    refetchGrid();
  });

  const handleScrollEnd = useEvent((): void => {
    if (!hasActiveGrouping) {
      return;
    }

    if (!fetchTableServerGroupValuesMoreIsAvailable()) {
      return;
    }

    fetchTableRowsAndGroupValues({
      isPreloader: true,
      isNeedToLoadMoreGroupValues: true,
      isNotNeedToResetPagination: true,
    });
  });

  useEffect(() => {
    if (!companyId || !isGridReady || !isInitializing) {
      return;
    }

    setIsInitializing(false);

    startTransition(() => {
      refetchGrid({ skipPageReset: true });
    });
    // isInitializing is read here and then cleared; listing it runs this effect twice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, isGridReady, refetchGrid]);

  useEffect(() => {
    if (isInitializing) {
      return;
    }

    startTransition(() => {
      refetchGrid();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableFilters]);

  useEffect(() => {
    if (isInitializing) {
      return;
    }

    startTransition(() => {
      refetchGrid({ skipPageReset: true });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableItemPagination]);

  useEffect(() => {
    if (isInitializing) {
      return;
    }

    startTransition(() => {
      refetchGrid();
    });
    // isInitializing is omitted so leaving init does not refetch on top of the first effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnSorting, columnGrouping]);

  const handlePaginationChange = useEvent(
    (groupPagination: IPaginationChangeEvent) => {
      if (!hasActiveGrouping) {
        return;
      }

      fetchTableRowsServerFiltered({
        isPreloader: true,
        groupPagination,
        isNotNeedToResetPagination: true,
      });
    },
  );

  useEmitterListener({
    event: GlobalAppEventEnum.PAGINATION_CHANGE,
    handler: handlePaginationChange,
    isEnabled: hasActiveGrouping,
  });

  return {
    onFiltersApply,
    onScrollEnd: handleScrollEnd,
  };
};
